// Components/Services/ADUAndCustomHomes/ProcessSection.jsx
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiHome, FiTool, FiCompass, FiUsers, FiSettings, FiCheckCircle } from "react-icons/fi";

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const stepVariants = {
  hidden: { opacity: 0, x: 24 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.45, ease: "easeOut" } },
};

const aduSteps = [
  {
    number: "01",
    title: "Free Consultation & Site Evaluation",
    desc: "We visit your property, assess feasibility, review zoning and setback requirements, and give you an honest picture of what's possible.",
    icon: <FiCompass className="w-5 h-5 text-white" />,
  },
  {
    number: "02",
    title: "Design & Architectural Plans",
    desc: "We coordinate with licensed architects to produce permit-ready drawings tailored to your property — detached unit, attached suite, or garage conversion.",
    icon: <FiUsers className="w-5 h-5 text-white" />,
  },
  {
    number: "03",
    title: "Permitting & City Approval",
    desc: "We handle all permit submissions, plan check responses, and city or county approvals in-house. You don't chase paperwork — we do.",
    icon: <FiSettings className="w-5 h-5 text-white" />,
  },
  {
    number: "04",
    title: "Construction & Site Management",
    desc: "Foundation, framing, MEP systems, insulation, drywall, finishes — every phase managed by our team with regular progress updates.",
    icon: <FiTool className="w-5 h-5 text-white" />,
  },
  {
    number: "05",
    title: "Final Inspection & Handoff",
    desc: "We walk the finished unit with you, address punch-list items, and hand over a fully permitted, move-in ready space.",
    icon: <FiCheckCircle className="w-5 h-5 text-white" />,
  },
];

const customSteps = [
  {
    number: "01",
    title: "Vision & Budget Consultation",
    desc: "We sit down with you to understand your lifestyle, goals, and budget — and give you a realistic investment range before any drawings are made.",
    icon: <FiCompass className="w-5 h-5 text-white" />,
  },
  {
    number: "02",
    title: "Architect & Engineering Coordination",
    desc: "We coordinate with licensed architects and structural engineers to translate your vision into full construction documents.",
    icon: <FiUsers className="w-5 h-5 text-white" />,
  },
  {
    number: "03",
    title: "Full Permitting Package",
    desc: "We submit your complete permit package, manage plan check corrections, and obtain all required approvals before breaking ground.",
    icon: <FiSettings className="w-5 h-5 text-white" />,
  },
  {
    number: "04",
    title: "Phased Construction with Updates",
    desc: "Site prep, foundation, framing, rough MEP, insulation, drywall, finish work — each phase executed with milestone check-ins.",
    icon: <FiTool className="w-5 h-5 text-white" />,
  },
  {
    number: "05",
    title: "Final Walkthrough & Key Handoff",
    desc: "We complete every punch-list item to your satisfaction, pass all final inspections, and hand over the keys to your finished home.",
    icon: <FiCheckCircle className="w-5 h-5 text-white" />,
  },
];

const tabs = [
  { id: "adu",    label: "ADU Build",        icon: <FiHome className="w-4 h-4" />, steps: aduSteps,    image: "/Images/ADUs.png",             badge: "Permitted ADU Build"    },
  { id: "custom", label: "Custom Home Build", icon: <FiTool className="w-4 h-4" />, steps: customSteps, image: "/Images/Behind-the-Scenes/BTS-5.jpeg", badge: "Ground-Up Custom Build" },
];

const ProcessSection = () => {
  const [active, setActive] = useState("adu");
  const current = tabs.find((t) => t.id === active);

  return (
    <section className="bg-white px-6 pt-10 pb-10 md:pt-14 md:pb-14">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <motion.div
          className="text-center mb-6"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-[#6c757d] uppercase tracking-wider text-xs font-semibold mb-1">
            How We Work
          </p>
          <h2 className="text-2xl md:text-3xl font-bold text-[#212529]">
            Our Step-by-Step Process
          </h2>
          <p className="text-[#6c757d] text-sm max-w-xl mx-auto mt-2">
            Every project follows a structured, transparent process — no surprises, no hand-offs, no guesswork.
          </p>
        </motion.div>

        {/* Tabs */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex bg-[#f8f9fa] border border-[#dee2e6] rounded-full p-1 gap-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActive(tab.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 ${
                  active === tab.id
                    ? "bg-[#212529] text-white shadow"
                    : "text-[#495057] hover:text-[#212529]"
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Dark Card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          >
            <div className="bg-[#212529] rounded-2xl overflow-hidden flex flex-col md:flex-row shadow-2xl">

              {/* Left — image */}
              <div className="md:w-[36%] w-full flex-shrink-0 p-4">
                <img
                  src={current.image}
                  alt={current.label}
                  className="w-full h-full object-cover rounded-xl"
                  style={{ minHeight: "320px", maxHeight: "460px" }}
                />
              </div>

              {/* Right — steps */}
              <div className="flex-1 flex flex-col justify-between px-6 py-5 md:px-8 md:py-6">
                <motion.div
                  key={active + "-steps"}
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                  className="flex flex-col"
                >
                  {current.steps.map((step, idx) => (
                    <motion.div
                      key={idx}
                      variants={stepVariants}
                      className="flex items-center gap-4 px-3 py-2.5 rounded-xl cursor-default
                                 transition-all duration-300 ease-in-out
                                 hover:bg-white/[0.06] hover:scale-[1.015] hover:shadow-[0_0_0_1px_rgba(255,255,255,0.08)]
                                 group"
                    >
                      {/* Number */}
                      <span className="text-2xl md:text-3xl font-bold text-white/20 w-10 flex-shrink-0 leading-none
                                       group-hover:text-white/50 transition-colors duration-300">
                        {step.number}
                      </span>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-white font-semibold text-sm md:text-base leading-snug
                                       group-hover:text-white transition-colors duration-300">
                          {step.title}
                        </h3>
                        <p className="text-white/45 text-xs md:text-sm mt-0.5 leading-relaxed
                                      group-hover:text-white/70 transition-colors duration-300">
                          {step.desc}
                        </p>
                      </div>

                      {/* Icon */}
                      <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center
                                      group-hover:bg-white/20 group-hover:scale-110 transition-all duration-300">
                        {step.icon}
                      </div>
                    </motion.div>
                  ))}
                </motion.div>

                {/* Bottom CTA */}
                <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-4 border-t border-white/10">
                  <p className="text-white/55 text-xs max-w-xs leading-relaxed">
                    Ready to start your {active === "adu" ? "ADU" : "custom home"} project?{" "}
                    <span className="text-white font-medium">Let's build something great together.</span>
                  </p>
                  <a
                    href="/contact"
                    className="inline-block bg-white text-[#212529] text-xs font-semibold px-5 py-2.5 rounded-full
                               hover:bg-[#f1f3f5] hover:shadow-md transition-all duration-200 whitespace-nowrap"
                  >
                    Get a Free Estimate
                  </a>
                </div>
              </div>

            </div>
          </motion.div>
        </AnimatePresence>

      </div>
    </section>
  );
};

export default ProcessSection;